import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Check } from "lucide-react";
import { SiteLayout } from "@/layouts/site-layout";
import { SEO } from "@/components/SEO";

const Agency = () => {
  const benefits = [
    {
      title: "Speed Without Compromise",
      description: "Deliver client MVPs in 7 days while maintaining your brand and quality standards."
    },
    {
      title: "White-Label Ready",
      description: "We work behind the scenes. You present the work. Your client never knows we're involved."
    },
    {
      title: "Scalable Capacity",
      description: "Take on more clients without hiring full-time developers or stretching your team thin."
    },
    {
      title: "AI-Accelerated Workflows",
      description: "We use AI tools to speed up development without sacrificing code quality or maintainability."
    }
  ];

  const capabilities = [
    "Client MVPs (SaaS, marketplaces, tools)",
    "Rapid prototyping for pitches and proposals",
    "Technical feasibility builds",
    "White-label development for rebrand",
    "AI feature integration for existing products"
  ];

  const processSteps = [
    {
      step: "Step 1",
      title: "Partnership Kickoff",
      description: "We discuss your client needs, timelines, and branding requirements."
    },
    {
      step: "Step 2",
      title: "Scope & Quote",
      description: "We provide a scoped estimate based on complexity and deliverables."
    },
    {
      step: "Step 3",
      title: "Build Sprint",
      description: "We build under your brand guidelines. You maintain client communication."
    },
    {
      step: "Step 4",
      title: "Handoff & Launch",
      description: "We deliver the MVP with full documentation. You launch with your client."
    }
  ];

  const targetAudience = [
    "Design agencies who need dev capacity",
    "Marketing agencies building client tools",
    "Startup studios launching portfolio companies",
    "Dev shops overbooked with projects",
    "Consultants pitching technical solutions"
  ];

  const models = [
    {
      title: "Project-Based",
      description: "Pay per MVP build. No long-term commitment."
    },
    {
      title: "Monthly Retainer",
      description: "Reserve capacity for ongoing client work."
    },
    {
      title: "Revenue Share",
      description: "Build equity-based partnerships for co-developed products."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Agency Partnership MVP Development",
    "description": "White-label MVP builds for design agencies, studios, and dev shops who want to deliver faster without sacrificing quality.",
    "provider": {
      "@type": "Organization",
      "name": "OneWeekMVPs"
    },
    "serviceType": "MVP Development Partnership"
  };

  return (
    <SiteLayout>
      <SEO
        title="Agency Partnership for MVP Development | White-Label MVP Build Service"
        description="Partner with us to deliver MVPs faster. MVP development partner for design agencies. Scale your agency offering with MVP development partner. White-label MVP builds without hiring overhead."
        keywords="agency partnership MVP development, MVP development partner for design agencies, partner with an MVP build service for agencies, scale your agency offering with MVP development partner, design agency partner with MVP developer to upsell clients without hiring, software studio partnership for rapid MVP builds and prototypes, agency partner offer: flat fee MVP development for your clients"
        canonicalUrl={`${typeof window !== 'undefined' ? window.location.origin : 'https://oneweekmvps.com'}/agency`}
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading leading-tight">
              Partner with us to <span className="text-primary">deliver faster</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              White-label MVP builds for agencies, studios, and dev shops who want to move at startup speed without sacrificing quality.
            </p>
            <Button size="lg" className="text-base px-8" asChild>
              <a href="https://cal.com/buildwithrishi/15min">Explore Collaboration</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Collaborate */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading">
                Why agencies partner with <span className="text-primary">OneWeekMVPs</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {benefits.map((benefit, idx) => (
                <Card key={idx} className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-2xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Capabilities */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                What we build for <span className="text-primary">agency partners</span>
              </h2>
            </div>

            <Card className="border-border/50">
              <CardContent className="pt-8">
                <ul className="space-y-4">
                  {capabilities.map((capability, idx) => (
                    <li key={idx} className="flex gap-3">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-lg text-muted-foreground">{capability}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How Collaboration Works */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                How <span className="text-primary">collaboration works</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, idx) => (
                <Card key={idx} className="border-border/50">
                  <CardHeader>
                    <div className="text-primary text-sm font-medium mb-2">{step.step}</div>
                    <CardTitle className="text-xl">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center pt-8">
              <Button size="lg" className="text-base px-8" asChild>
                <a href="https://cal.com/buildwithrishi/15min">Book a Partnership Call</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                This collaboration <span className="text-primary">works best for:</span>
              </h2>
            </div>

            <Card className="border-border/50">
              <CardContent className="pt-8">
                <ul className="space-y-4">
                  {targetAudience.map((item, idx) => (
                    <li key={idx} className="flex gap-3">
                      <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-lg text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Collaboration Models */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                Flexible <span className="text-primary">partnership options</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {models.map((model, idx) => (
                <Card key={idx} className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-2xl">Model {idx + 1}</CardTitle>
                    <CardTitle className="text-xl text-primary">{model.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{model.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center pt-8">
              <Button size="lg" variant="outline" className="text-base px-8" asChild>
                <a href="https://cal.com/buildwithrishi/15min">Discuss Your Model</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="text-2xl md:text-3xl font-heading text-foreground/90 italic">
              "OneWeekMVPs became our secret weapon. We pitch faster, deliver faster, and clients never know we're not doing it all in-house."
            </blockquote>
            <p className="mt-6 text-muted-foreground">
              — Digital Agency Partner
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading">
              Let's build <span className="text-primary">faster together</span>
            </h2>
            <Button size="lg" className="text-base px-8" asChild>
              <a href="https://cal.com/buildwithrishi/15min">Schedule a Partnership Call</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Agency;
